import { Cookies, Notify, Platform } from 'quasar'
import { $rollbar } from '@/boot/rollbar'
import { phoneNumberE164, phoneNumberNational, httpsUrl } from '@/utils/formatter.util'
import { PEG_RETAILER_AUTH_COOKIE } from '@/constants/cookies.const'

let dismissSessionExpiredNotif = null
let dismissRetailerSessionExpiredNotif = null

export function notifySessionExpired() {
  if (dismissSessionExpiredNotif) return
  dismissSessionExpiredNotif = Notify.create({
    type: 'warning',
    message: 'Your session has expired. Refresh the page to continue.',
    timeout: 0,
    position: 'top',
    actions: [
      {
        label: 'Refresh',
        icon: 'fas fa-refresh',
        color: 'grey-9',
        handler: () => {
          location.reload()
        },
      },
    ],
    onDismiss: () => {
      dismissSessionExpiredNotif = null
    },
  })
}

export function notifyRetailerSessionExpired() {
  if (dismissRetailerSessionExpiredNotif) return
  dismissRetailerSessionExpiredNotif = Notify.create({
    type: 'warning',
    message: 'Your session has expired. Please re-launch the app from Shopify to continue.',
    timeout: 0,
    position: 'top',
    onDismiss: () => {
      dismissRetailerSessionExpiredNotif = null
    },
  })
}

export function dismissSessionExpired() {
  if (dismissSessionExpiredNotif) {
    dismissSessionExpiredNotif()
    dismissSessionExpiredNotif = null
  }
  if (dismissRetailerSessionExpiredNotif) {
    dismissRetailerSessionExpiredNotif()
    dismissRetailerSessionExpiredNotif = null
  }
}

function parsePegRequestBody(body) {
  // nullify empty strings and undefined values
  Object.keys(body).forEach(key => {
    if (body[key] === '' || body[key] === undefined) {
      body[key] = null
    }
  })

  // format phone number to E.164 format for DB storage
  if (body.phone) {
    body.phone = phoneNumberE164(body.phone) || null
  }

  // format url to https if it exists
  if (body.website) {
    body.website = httpsUrl(body.website)
  }

  return body
}

function parsePegResponse(data) {
  // recursively parse nested objects and arrays
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      data = data.map(item => parsePegResponse(item))
    } else {
      Object.keys(data).forEach(key => {
        if (typeof data[key] === 'object' && data[key] !== null) {
          data[key] = parsePegResponse(data[key])
        }
      })
    }
  }
  // format phone number to national format for display
  if (data.phone) {
    data.phone = phoneNumberNational(data.phone)
  }

  return data
}

/**
 * Standardized fetch wrapper
 * @param {String} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {Boolean} options.throwErrors - If true, throws errors (default). If false, returns error objects with { success: false }
 * @param {Boolean} options.silent - If true, suppresses internal Rollbar logging (caller handles logging)
 * @param {String} options.credentials - Optional credentials mode: 'include' (sends cookies cross-origin), 'same-origin', or 'omit'
 * @param {Boolean} options.keepalive - If true, sets fetch `keepalive` so the request survives a page unload (e.g. a fire-and-forget POST fired immediately before a full-page redirect). Use for small best-effort beacons only — keepalive bodies are capped (~64KB) by the browser.
 */
function doFetch(url, options = {}) {
  let {
    method = 'GET',
    headers = {},
    body = null,
    params = null,
    raw = false,
    throwErrors = true,
    silent = false,
    credentials,
    keepalive = false,
  } = options

  // Only JSON.stringify if body is not FormData
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(body)
  }

  if (params) {
    // stringify params object
    const searchParams = new URLSearchParams()
    for (const key in params) {
      searchParams.append(key, params[key])
    }
    // append to endpoint
    url += `?${searchParams.toString()}`
  }

  // PEG-509: pass through `credentials` so cookie-based admin endpoints can
  // opt into `'include'` for cross-origin XHR. Other call sites get the fetch
  // default ('same-origin'), which preserves prior behavior.
  const fetchOptions = { method, headers, body }
  if (credentials) fetchOptions.credentials = credentials
  // PEG-845 (AC18): opt-in keepalive lets a fire-and-forget beacon (e.g. the
  // repurchase-CTA click tracker) complete even when the click immediately
  // triggers a full-page redirect (window.location.assign) that would otherwise
  // cancel the in-flight request and drop the attribution event.
  if (keepalive) fetchOptions.keepalive = true

  return fetch(url, fetchOptions)
    .then(res => {
      return res.text().then(text => {
        try {
          return JSON.parse(text)
        } catch {
          throw {
            statusCode: res.status,
            nonJson: true,
            message: `Non-JSON response from ${method} ${url} (HTTP ${res.status})`,
            responseBody: text?.slice(0, 500),
          }
        }
      })
    })
    .then(res => {
      if (res.statusCode >= 400) {
        throw res
      }

      // optionally return full response
      if (raw) {
        return { ...res, success: true }
      }

      // only return data object
      return { ...res.data, success: true, statusCode: res.statusCode }
    })
    .catch(error => {
      // Skip logging for expected non-error statuses. 401/404 and an
      // expired-session message are normal control-flow signals, not faults.
      // PEG-915 (AC11): a 409 `conversation_sealed` is the backend's
      // per-conversation gate firing on an already-sealed conversation — it is
      // expected control flow the chat composable handles gracefully (it seals
      // the input and shows the "Start a new chat" banner), so it must not be
      // logged as an application error or surface as Rollbar/console noise.
      const skipLog =
        ([401, 404].includes(error?.statusCode) && !error?.nonJson) ||
        (error?.statusCode === 409 && error?.error === 'conversation_sealed') ||
        ['expired'].includes(error?.message?.toLowerCase())

      if (!skipLog && !silent) {
        // Categorize error type for better Rollbar grouping
        const errorType =
          error instanceof TypeError
            ? 'Network error'
            : error?.name === 'AbortError'
              ? 'Request aborted'
              : error?.statusCode >= 500
                ? `Server error (${error.statusCode})`
                : error?.statusCode >= 400
                  ? `Client error (${error.statusCode})`
                  : 'Fetch error'

        const context = {
          url,
          method,
          statusCode: error?.statusCode ?? null,
          message: error?.message || (typeof error === 'string' ? error : 'Unknown'),
          online: typeof navigator !== 'undefined' ? navigator.onLine : null,
          responseSnippet:
            error?.responseBody ?? (typeof error === 'object' && error !== null ? JSON.stringify(error).slice(0, 500) : null),
        }

        $rollbar.error(`${errorType}: ${method} ${url}`, context)
      }

      if (throwErrors) {
        throw error
      } else {
        return { ...error, success: false }
      }
    })
}

/**
 * Fetch data from the PEG API
 * @param {String} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @param {Boolean} options.throwErrors - If true, throws errors (default). If false, returns error objects with { success: false }
 * @returns {Promise} - API response
 */
async function peg(endpoint, options = {}) {
  const sessionStore = useSessionStore()
  const returnSessionStore = useCustomerReturnSessionStore()
  const url = `${import.meta.env.VITE_PEG_API_URL}${endpoint}`

  // allow retry on 401
  options.retryOnAuthFailure = options.retryOnAuthFailure !== false

  // $logger.debug(`API Request: ${options.method || 'GET'} ${url}`)

  if (options.body) {
    options.body = parsePegRequestBody(options.body)
  }

  options.headers = {
    ...(options.headers || {}),
    'x-app-id': import.meta.env.VITE_PEG_API_APP_ID,
    'x-platform': Platform.is.nativeMobile ? 'native' : 'web',
    'ngrok-skip-browser-warning': 'true', // Skip ngrok browser warning
  }

  if (endpoint.startsWith('/retailers')) {
    options.headers['x-access-token'] = Cookies.get(PEG_RETAILER_AUTH_COOKIE)
    // PEG-509: internal admins viewing a retailer dashboard authenticate via
    // the HttpOnly peg_admin_session cookie (no peg_retailer_auth JWT). The
    // browser only attaches the admin cookie to cross-origin /retailers calls
    // when credentials: 'include' is set; setting it here is harmless for
    // regular retailers (they continue to authenticate via x-access-token).
    options.credentials = options.credentials || 'include'
  }

  if (sessionStore.sessionAuthToken) {
    options.headers['x-id-token'] = sessionStore.sessionAuthToken
  }

  if (returnSessionStore.returnSessionId) {
    options.headers['x-return-session-id'] = returnSessionStore.returnSessionId
  }

  if (returnSessionStore.orderLookupToken) {
    options.headers['x-order-lookup-token'] = returnSessionStore.orderLookupToken
  }

  if (returnSessionStore?.currentItem?.lineItemSessionId) {
    options.headers['x-line-session-id'] = returnSessionStore.currentItem.lineItemSessionId
  }

  // Handle 401 / expired-session responses. Extracted so both the throwing
  // path (throwErrors: true → catch) and the non-throwing path
  // (throwErrors: false → !response.success) run the same logic:
  // admin redirect, retailer notification, or token-refresh retry.
  async function handleExpiredSession(error) {
    const expiredResponse = { ...error, success: false, sessionExpired: true }
    const adminStore = useAdminStore()

    if (endpoint.startsWith('/admin')) {
      if (adminStore.hasAdminMarker()) {
        adminStore.handleSessionExpired()
        window.location.href = '/admin/expired?reason=expired'
      }
      // else noop — 401 from admin endpoint without marker means user isn't admin
    } else if (endpoint.startsWith('/retailers')) {
      if (adminStore.hasAdminMarker()) {
        // PEG-509: admin sessions hit /retailers endpoints via HttpOnly cookie.
        // When that cookie expires, route through admin-expired flow — not the
        // Shopify "re-launch" notification meant for regular retailers.
        adminStore.handleSessionExpired()
        window.location.href = '/admin/expired?reason=expired'
      } else {
        // Retailer endpoints use Shopify auth — Firebase refresh won't help
        notifyRetailerSessionExpired()
      }
    } else if (options.retryOnAuthFailure && sessionStore.sessionAuthToken) {
      const isRefreshed = await sessionStore.refreshAuth()

      if (isRefreshed) {
        options.retryOnAuthFailure = false // prevent infinite retry loop
        // No need to clear headers — peg() rebuilds all auth headers
        // (x-id-token, x-access-token, etc.) at the top of each call,
        // overwriting stale values. Custom caller headers are preserved.
        return { retryResult: await peg(endpoint, options) }
      } else {
        notifySessionExpired()
      }
    } else {
      notifySessionExpired()
    }

    return { expiredResponse }
  }

  function isExpiredSessionError(err) {
    return err?.statusCode === 401 || ['expired token', 'expired'].includes(err?.message?.toLowerCase())
  }

  try {
    let response = await doFetch(url, options)

    if (!response.success) {
      // throwErrors: false — doFetch returned the error instead of throwing.
      // Still need to handle 401s so token-refresh and session-expired
      // notifications fire for callers that opted out of exceptions.
      if (isExpiredSessionError(response)) {
        const result = await handleExpiredSession(response)
        if (result.retryResult) return result.retryResult
        return result.expiredResponse
      }
      return response
    }

    if (response.data) {
      response.data = parsePegResponse(response.data)
    } else {
      // if no data, just return the response as is
      response = parsePegResponse(response)
    }

    return response
  } catch (error) {
    if (isExpiredSessionError(error)) {
      const result = await handleExpiredSession(error)
      if (result.retryResult) return result.retryResult
      throw result.expiredResponse
    }

    throw error
  }
}

/**
 * Quick connectivity check against the PEG API.
 * Retries up to 3 times with exponential backoff before declaring unreachable.
 * Resolves true if reachable, false otherwise.
 */
async function checkApiConnection(timeoutMs = 4000, maxRetries = 3) {
  const url = import.meta.env.VITE_PEG_API_URL

  let lastError = null
  let lastStatus = null

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const res = await fetch(`${url}/ping`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'x-app-id': import.meta.env.VITE_PEG_API_APP_ID,
        },
      })
      if (res.ok) return true
      lastStatus = res.status
    } catch (err) {
      lastError = err
    } finally {
      clearTimeout(timer)
    }

    // Exponential backoff between retries (default waits: 1s, 2s)
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)))
    }
  }

  $rollbar.error('PEG API connectivity check failed after retries', {
    url: `${url}/ping`,
    timeoutMs,
    maxRetries,
    lastStatus,
    errorName: lastError?.name,
    errorMessage: lastError?.message,
  })
  return false
}

export const $fetch = {
  fetch: doFetch,
  peg,
  checkApiConnection,
}
